import React, { useContext, useEffect, useRef, useState } from "react";
import { MyContext } from "../../Context/MyProvider";
import axios from "axios";
import Loading from "../Loading";
import secureLocalStorage from "react-secure-storage";
import { LuCalendar, LuHeart, LuQrCode, LuRefreshCw } from "react-icons/lu";
import { IoCloseSharp } from "react-icons/io5";
import { useTranslation } from "react-i18next";

const DigitalHealthCard = ({ patientData }) => {
  const { t } = useTranslation();
  const { ApiBaseUrl } = useContext(MyContext);

  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState("");
  const [isFlipped, setIsFlipped] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);

  const FetchRef = useRef(false);

  const verifyLink = `${ApiBaseUrl}/patient/card/verify?token=${token}`;
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(verifyLink)}&color=0f172a`;

  useEffect(() => {
    console.log(patientData);

    if (!FetchRef.current) {
      FetchRef.current = true;

      const loadCardDetails = async () => {
        const Userid = secureLocalStorage.getItem("Userid");

        try {
          const linkRes = await axios.post(
            `${ApiBaseUrl}/customer/digital_card_link`,
            {
              id: Userid,
              target_id: patientData?.id,
              target_type: "family",
            },
          );
          if (linkRes.data.Result === true) {
            const urlObj = new URL(linkRes.data.digital_card_url);
            setToken(urlObj.searchParams.get("token"));
          }
        } catch (error) {
          console.error("Error fetching health card details:", error);
        } finally {
          setLoading(false);
        }
      };

      loadCardDetails();
    }
  }, []);

  return (
    <>
      {loading ? (
        <div className="h-[400px] flex items-center justify-center">
          <Loading
            CustomeStyle="w-full h-full flex items-center justify-center bg-white z-[9999]"
            index={1}
          />
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center max-w-md mx-auto select-none">
          <div className="w-full mb-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-slate-800 tracking-tight">
                {t("Digital Health Pass")}
              </h2>
              <p className="text-xs text-slate-450 mt-0.5 font-medium">
                {t("Click the card to flip it over. Click QR to zoom & scan.")}
              </p>
            </div>
          </div>

          <div
            className="cursor-pointer relative rounded-[16px] mb-6 shadow-[0_15px_35px_rgba(66,92,244,0.22)]"
            onClick={() => setIsFlipped(!isFlipped)}
            style={{ width: "360px", height: "220px", perspective: "1000px" }}
          >
            <div
              className="w-full h-full duration-700 relative"
              style={{
                transformStyle: "preserve-3d",
                transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
              }}
            >
              {/* Front Face */}
              <div
                className="absolute inset-0 w-full h-full p-[22px] rounded-[16px] text-white flex flex-col justify-between overflow-hidden border border-white/10"
                style={{
                  backfaceVisibility: "hidden",
                  background:
                    "linear-gradient(135deg, rgba(30, 64, 175, 0.9), rgba(109, 40, 217, 0.85))",
                }}
              >
                <div className="w-full h-full flex flex-col justify-between relative z-10">
                  <div className="flex justify-between items-center">
                    <span className="font-bold tracking-wider text-[15px] text-white font-sans">
                      {t("DOCTORRHYNOR PASS")}
                    </span>
                    <LuHeart className="w-[18px] h-[18px] text-white fill-white shrink-0 filter drop-shadow-sm" />
                  </div>

                  <div className="flex justify-start items-center">
                    <div className="w-[42px] h-[32px] bg-gradient-to-br from-[#ffd700] to-[#b8860b] rounded-[6px] px-[6px] py-[4px] flex items-center justify-center relative">
                      <div className="w-full h-full rounded-[3.5px] border border-[#00000040]" />
                    </div>
                  </div>

                  <div>
                    <div
                      style={{
                        fontFamily: "'Courier New', Courier, monospace",
                        letterSpacing: "2.5px",
                      }}
                      className="text-[19px] font-bold text-white uppercase mt-[12px] mb-[6px]"
                    >
                      {" "}
                      {patientData?.digital_card_id}
                    </div>

                    <div className="text-left">
                      <div className="text-white text-[13px] font-[700] uppercase">
                        {patientData.name}
                      </div>

                      <div className="grid grid-cols-3 gap-[10px] mt-[4px]">
                        <div className="text-[11px] uppercase">
                          <div className="text-[#ffffffd9]">{t("Gender")}</div>
                          <div className="text-[12px] font-bold text-white uppercase block mt-0.5">
                            {patientData.gender || "MALE"}
                          </div>
                        </div>
                        <div className="text-[11px] uppercase">
                          <div className="text-[#ffffffd9]">{t("Age / DOB")}</div>
                          <div className="text-[12px] font-bold text-white uppercase block mt-0.5">
                            {patientData.patient_age || patientData.age || "23"}
                          </div>
                        </div>
                        <div className="text-[11px] uppercase">
                          <div className="text-[#ffffffd9]">{t("Blood")}</div>
                          <div className="text-[12px] font-bold text-white uppercase block mt-0.5">
                            {patientData.blood_group || "O+"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Back Face */}
              <div
                className="absolute inset-0 w-full h-full p-[22px] rounded-[16px] text-white flex flex-col justify-between overflow-hidden border border-white/10"
                style={{
                  backfaceVisibility: "hidden",
                  transform: "rotateY(180deg)",
                  background:
                    "linear-gradient(135deg, rgba(109, 40, 217, 0.9), rgba(30, 64, 175, 0.85))",
                }}
              >
                <div className="absolute inset-0 bg-white/[0.03] backdrop-blur-[0.5px] pointer-events-none" />
                <div className="absolute -top-24 -right-24 w-56 h-56 bg-white/10 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-16 -left-16 w-44 h-44 bg-blue-400/20 rounded-full blur-2xl pointer-events-none" />

                <div className="w-full h-full flex flex-col justify-between relative z-10">
                  <div className="flex justify-between items-center">
                    <span className="font-bold tracking-wider text-[15px] text-white uppercase">
                      {t("EMERGENCY PASS")}
                    </span>
                    <span className="font-bold tracking-wider text-[9px] text-white/85 uppercase">
                      {t("CLINIC CHECK-IN")}
                    </span>
                  </div>

                  <div className="flex gap-5 items-center justify-center">
                    <div
                      className="bg-white p-[5px] rounded-[8px] shadow-lg shrink-0 cursor-zoom-in hover:scale-105 transition-all duration-300"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsZoomed(true);
                      }}
                    >
                      <img
                        className="w-[90px] h-[90px] object-contain"
                        src={qrCodeUrl}
                        alt="Check-in QR"
                      />
                    </div>

                    <div className="flex-1 text-left min-w-0">

                      <div className="text-white text-[13px] font-semibold">
                        {t("Allergies")}:
                      </div>

                      <div className="block text-[13px] text-[#ffffffe6] truncate max-w-[200px] -mt-[2px]">
                        {patientData.allergies}
                      </div>

                      <div className="text-[13px] font-semibold text-white">
                        {t("Emergency Contact:")}
                      </div>

                      <div className=" text-[13px] text-[#ffffffe6] -mt-[2px]">
                        {patientData.emergency_name || "8585858585"}{" "}
                        {patientData.phone
                          ? `(${patientData.phone})`
                          : "(8989898989)"}
                      </div>

                      <div className="inline-block bg-[#ef444433] border border-[#ef444466] text-[#fca5a5] py-[2px] px-[6px] rounded-[4px] text-[10px] font-bold">
                        {t("MEDICAL RECORD QR")}
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="w-full">
                    <div className="border-t border-[#ffffff26] w-full my-2" />
                    <div className="text-center text-[9px] sm:text-[10px] text-white/80 font-medium tracking-wide">
                      {t("Hotline")}: +91 91561 00600
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full flex gap-3 px-1 mt-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsZoomed(true);
              }}
              className="flex-1 inline-flex items-center justify-center gap-2 py-3.5 px-5 rounded-2xl text-sm font-bold bg-[#425cf4] text-white hover:opacity-90 hover:shadow-md hover:shadow-indigo-500/10 active:scale-[0.98] transition-all duration-150 cursor-pointer"
            >
              <LuQrCode className="w-4.5 h-4.5" />
              {t("View Scan")}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsFlipped(!isFlipped);
              }}
              className="inline-flex items-center justify-center gap-2 py-3.5 px-5 rounded-2xl text-sm font-bold border border-slate-205 text-slate-700 bg-white hover:bg-slate-50 active:scale-[0.98] transition-all duration-150 cursor-pointer"
            >
              <LuRefreshCw className="w-4 h-4 text-slate-400" />
              {t("Flip Pass")}
            </button>
          </div>
        </div>
      )}

      {isZoomed && (
        <div
          className="fixed inset-0 z-[1010] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-200"
          onClick={() => setIsZoomed(false)}
        >
          <div
            className="bg-white rounded-[32px] p-8 text-center max-w-sm w-full shadow-2xl border border-slate-100 flex flex-col items-center gap-5 animate-in zoom-in duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-full flex justify-between items-center border-b border-slate-100 pb-3">
              <div className="text-left">
                <h3 className="text-lg font-black text-slate-800 tracking-tight">
                  {t("Check-in Scan Pass")}
                </h3>
                <p className="text-xs text-slate-450 mt-0.5">
                  {t("Present code to front-desk check-in scanner")}
                </p>
              </div>
              <button
                onClick={() => setIsZoomed(false)}
                className="p-1.5 rounded-full text-slate-450 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <IoCloseSharp className="w-5.5 h-5.5" />
              </button>
            </div>

            <div className="bg-slate-50 p-4.5 rounded-3xl border border-slate-100 shadow-inner flex items-center justify-center">
              <img
                className="w-48 h-48 object-contain"
                src={qrCodeUrl}
                alt="Check-in QR Large"
              />
            </div>

            <div className="w-full bg-slate-50 border border-slate-100 rounded-2xl p-3.5 text-left flex gap-3 items-center">
              <div className="w-9 h-9 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                <LuCalendar className="w-5 h-5" />
              </div>
              <div className="leading-tight">
                <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  {t("Patient Name")}
                </span>
                <span className="block text-sm font-black text-slate-700 uppercase mt-0.5">
                  {patientData.name}
                </span>
              </div>
            </div>

            <button
              onClick={() => setIsZoomed(false)}
              className="w-full py-3.5 bg-slate-900 hover:bg-slate-800 text-white rounded-2xl font-bold text-xs uppercase tracking-wider transition-colors shadow-md"
            >
              {t("Close Scan")}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default DigitalHealthCard;
